import {
  isPermissionNavRole as checkPermissionNavRole,
  userHasOrgAssignmentAll,
} from "@/contexts/use-auth";
import { useAuth } from "@/contexts/use-auth";
import { useNavigate, useLocation } from "react-router";
import { useEffect, useState } from "react";
import { ChildrenRenderer } from "@/components/ChildrenRenderer";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuBadge,
  SidebarFooter,
  SidebarInset,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import {
  Home,
  Building,
  ClipboardList,
  Users,
  Settings,
  LogOut,
  Heart,
  User,
  UserCheck,
  Stethoscope,
  HeartHandshake,
  FileText,
  HelpCircle,
  Smartphone,
  ArrowLeft,
  Pill,
  LayoutDashboard,
  Copy,
  Check,
  Bell,
  Inbox,
  Search,
  Ticket,
  UserCog,
  Repeat,
  History,
  Sparkles,
  GitCompare,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useOrg } from "@/contexts/useOrg";
import AxiosInstance from "@/utils/AxiosInstance";
import { toast } from "sonner";
import { useGetData } from "@/hooks/useGetData";
import { Input } from "@/components/ui/input";
import GlobalPatientSearch from "@/components/GlobalPatientSearch";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import UserSwitch from "./UserSwitch";
import MyDialogContent from "@/components/MyDialogContent";
import PatientHistoryTimeline from "@/components/patient/PatientHistoryTimeline";
import { HoverTooltip } from "@/components/ui/tooltip";

interface Organization {
  _id: string;
  name: string;
}

interface DashboardLayoutProps {
  className?: string;
  children:
    | React.ReactNode
    | ((selectedOrgId: string | null) => React.ReactNode);
  activePage?: string;
  rightcomponent?: React.ReactNode;
  entityId?: string;
  entityHeaderAfterId?: React.ReactNode;
}

function renderChildren(
  children:
    | React.ReactNode
    | ((selectedOrgId: string | null) => React.ReactNode),
  selectedOrgId: string | null,
): React.ReactNode {
  return typeof children === "function" ? children(selectedOrgId) : children;
}

export function DashboardLayout({
  children,
  className,
  activePage,
  rightcomponent,
  entityId,
  entityHeaderAfterId,
}: DashboardLayoutProps) {
  const {
    isLoading,
    isAuthenticated,
    user,
    displayName,
    signOut,
    isAuthorized,
    canAccess,
    profile,
    userSwitchSessionData,
    patientHasLegacyData,
  } = useAuth();
  const [copied, setCopied] = useState(false);
  const [isUserSwitchDialogOpen, setIsUserSwitchDialogOpen] = useState(false);
  const [isAdminHistoryOpen, setIsAdminHistoryOpen] = useState(false);
  const [isPatientHistoryOpen, setIsPatientHistoryOpen] = useState(false);
  const [isProviderHistoryOpen, setIsProviderHistoryOpen] = useState(false);
  const [isPractitionerHistoryOpen, setIsPractitionerHistoryOpen] =
    useState(false);
  const [isCaretakerHistoryOpen, setIsCaretakerHistoryOpen] = useState(false);
  const hasLinkedProfilesForSwitch =
    (userSwitchSessionData?.linkedProfiles?.length || 0) > 1;

  const handleCopyId = async () => {
    if (!entityId) return;
    try {
      await navigator.clipboard.writeText(entityId);
      setCopied(true);
      toast.success("ID copied to clipboard", {
        duration: 2000,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy ID");
    }
  };

  const {
    organizations,
    setSelectedOrgId,
    selectedOrgId,
    isAllSelected,
    resetOrgs,
  } = useOrg();

  const isAdminUser = user?.role === "admin";
  const adminHistoryOrganizationId =
    isAllSelected || !selectedOrgId ? null : selectedOrgId;

  /** API `canAccess*` sidebar — only manager / staff / coordinator. Other roles use legacy rules. */
  const isPermissionNavRole = checkPermissionNavRole(user?.role);

  const canViewOrganizationsInNav = isPermissionNavRole
    ? canAccess("canAccessOrganizations")
    : isAuthorized("canViewOrganizationsInSidebar");

  /** Admin legacy: Organizations page only when "Anthro Care (All)" is selected. Managers: permission only. */
  const showOrganizationsSidebarItem =
    canViewOrganizationsInNav && (isPermissionNavRole || isAllSelected);

  const showOrgSwitcher = canViewOrganizationsInNav || true;
  const showAllOrgsOption =
    user?.role === "admin" ||
    (isPermissionNavRole && userHasOrgAssignmentAll(user));

  const canAccessConfigNav =
    user?.role === "admin"
      ? isAllSelected
      : isPermissionNavRole && canAccess("canAccessConfig");

  /** Tenant managers use Config only — not admin User management. */
  const canAccessUserManagementNav = user?.role === "admin";

  const navigate = useNavigate();
  const location = useLocation();

  const isAdminDashboardPage =
    location.pathname === "/dashboard" || activePage === "/dashboard";

  // Check if current page is a main sidebar page (should not show back arrow)
  const isMainSidebarPage = () => {
    const currentPath = location.pathname;
    const isPatient = user?.role === "patient";
    const mainPages = [
      isPatient ? "/patient-dashboard" : "/dashboard",
      "/notifications",
      "/patients",
      "/providers",
      "/practitioners",
      "/ccm-patients",
      "/alerts",
      "/caretakers",
      "/organizations",
      "/config",
      "/patient-careplan",
      "/patient-manage-access",
      "/patient-support",
      "/patient-device",
      "/organization",
      "/support",
      "/user-management",
      "/providers/me/managers",
      "/legacy",
      "/ai-chat",
      "/conflicts",
    ];

    // Check exact match
    if (mainPages.includes(currentPath)) {
      return true;
    }

    // Check if it's a main page with exact path (not a sub-route)
    // e.g., /patients should show back arrow, but /patients/123 should not
    for (const page of mainPages) {
      if (currentPath === page) {
        return true;
      }
    }

    return false;
  };

  // Helper function to check if a path is active
  const isPathActive = (path: string) => {
    const currentPath = location.pathname;

    // Normalize paths - ensure they start with /
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;

    // Exact match
    if (currentPath === normalizedPath) {
      return true;
    }

    // Check if current path starts with the given path followed by / (for nested routes)
    // e.g., /patients/123 should match /patients, but /patient-careplan should NOT match /patients
    if (currentPath.startsWith(normalizedPath + "/")) {
      return true;
    }

    // Special case for dashboard - also match root path
    if (
      normalizedPath === "/dashboard" &&
      (currentPath === "/dashboard" || currentPath === "/")
    ) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/auth");
    }
  }, [isLoading, isAuthenticated, navigate]);

  // Handle selected org
  useEffect(() => {
    if (user && organizations && organizations.length > 0 && !selectedOrgId) {
      const savedOrgId = localStorage.getItem("selectedOrgId");
      console.log({ user, organizations, savedOrgId });

      if (savedOrgId && organizations.some((org) => org._id === savedOrgId)) {
        setSelectedOrgId(savedOrgId);
      } else {
        user?.role == "admin"
          ? setSelectedOrgId("anthro-care")
          : setSelectedOrgId(organizations?.[0]?._id);
      }
    }
  }, [organizations, user]);

  // Persist selected organization to localStorage
  useEffect(() => {
    if (selectedOrgId) {
      localStorage.setItem("selectedOrgId", selectedOrgId);
    }
  }, [selectedOrgId]);

  const isProviderOrPractitioner =
    user?.role === "provider" || user?.role === "practitioner";

  const currentPatient = user?.role === "patient" ? user?.email : null;

  const displayRole =
    user?.role === "caretaker"
      ? "Caretaker"
      : user?.role === "patient"
        ? "Patient"
        : user?.role === "provider"
          ? "Provider"
          : user?.role || "Healthcare Provider";
  const IS_PATIENT = user?.role === "patient";
  const IS_PROVIDER = user?.role === "provider";
  const IS_PRACTITIONER = user?.role === "practitioner";
  const IS_CARETAKER = user?.role === "caretaker";
  const patientHistoryId = IS_PATIENT
    ? String(
        (profile as { _id?: string; id?: string } | null | undefined)?._id ??
          profile?.id ??
          "",
      ).trim()
    : "";
  const providerHistoryId = IS_PROVIDER
    ? String(
        (profile as { _id?: string; id?: string } | null | undefined)?._id ??
          profile?.id ??
          "",
      ).trim()
    : "";
  const practitionerHistoryId = IS_PRACTITIONER
    ? String(
        (profile as { _id?: string; id?: string } | null | undefined)?._id ??
          profile?.id ??
          "",
      ).trim()
    : "";
  const caretakerHistoryId = IS_CARETAKER
    ? String(
        (profile as { _id?: string; id?: string } | null | undefined)?._id ??
          profile?.id ??
          "",
      ).trim()
    : "";
  const nav = useNavigate();
  const [sidebarAlertRefreshKey, setSidebarAlertRefreshKey] = useState(0);

  //can be replace with Websocket
  useEffect(() => {
    if (IS_PATIENT || user?.role === "portal_access") return;

    const intervalId = window.setInterval(() => {
      setSidebarAlertRefreshKey((prev) => prev + 1);
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, [IS_PATIENT, user?.role]);

  const isMobile = useIsMobile();
  const onCallReportPage = () => {
    const orgId = selectedOrgId === "anthro-care" ? "all" : selectedOrgId;
    console.log({ orgId });
    navigate("/organization/" + orgId + "/reports");
  };

  // Fetch critical records count for sidebar badge
  const { apiData: criticalReadingsData } = useGetData(
    "/critical-readings",
    {
      search: "",
      status: "pending",
      refreshKey: sidebarAlertRefreshKey,
      page: 1,
      limit: 10,
      skip: 0,
      sortKey: "",
      sortType: "",
      ...(selectedOrgId && selectedOrgId !== "anthro-care"
        ? { organizationId: selectedOrgId }
        : {}),
    },
    false,
    !IS_PATIENT && user?.role !== "portal_access",
    () => {},
  );

  // Get the count from the API response
  // console.log({criticalReadingsData});
  const criticalRecordsCount =
    criticalReadingsData?.data?.pagination?.total ||
    criticalReadingsData?.data?.total ||
    criticalReadingsData?.data?.alerts?.length ||
    0;

  // ========= Default Sidebar (non-patient) =========
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <SidebarProvider
      defaultOpen={true}
      className="w-full max-w-full overflow-x-hidden overflow-y-hidden"
    >
      <Sidebar className="bg-white" side={"left"}>
        <SidebarHeader className="px-1 py-1.5 sm:py-4.5 bg-white">
          <div
            className="flex items-center justify-center cursor-pointer "
            onClick={() => navigate("/")}
          >
            <img
              src={isMobile ? "/logo.png" : "/logo.png"}
              className="w-32 md:w-full"
              alt="Logo"
            />
          </div>
        </SidebarHeader>

        <Separator />

        <SidebarContent className="p-0 py-0 pb-4 bg-white teachers">
          <SidebarMenu>
            {!IS_PATIENT && user?.role != "portal_access" && (
              <div className="px-1 py-4">
                {!showOrgSwitcher ? (
                  <div className="w-full text-sm px-1 py-1.5  border border-primary rounded-md truncate">
                    <p className="font-medium text-primary">
                      {organizations[0]?.organizationName}
                    </p>
                  </div>
                ) : (
                  <div className="px-1">
                    <Select
                      value={selectedOrgId || undefined}
                      onValueChange={(value) => {
                        setSelectedOrgId(value);
                        navigate("/dashboard");
                      }}
                    >
                      <SelectTrigger className="w-full text-sm border border-primary text-primary max-h-8 [&>svg]:hidden px-1">
                        <SelectValue placeholder="Select organization" />
                      </SelectTrigger>
                      <SelectContent className="">
                        {showAllOrgsOption && (
                          <SelectItem value="anthro-care">
                            Anthro Care (All)
                          </SelectItem>
                        )}
                        {organizations.map((org) => (
                          <SelectItem key={org._id} value={org._id}>
                            {org.organizationName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            <SidebarMenuItem className="">
              <SidebarMenuButton
                isActive={isPathActive(
                  IS_PATIENT ? "/patient-dashboard" : "/dashboard",
                )}
                onClick={() =>
                  navigate(IS_PATIENT ? "/patient-dashboard" : "/dashboard")
                }
                className={`w-full rounded-none h-16 p-0 ${
                  isPathActive(IS_PATIENT ? "/patient-dashboard" : "/dashboard")
                    ? "bg-primary/10 text-primary"
                    : ""
                }`}
              >
                <div className="flex flex-row md:flex-col items-center justify-center gap-2 md:gap-1 w-full h-full">
                  <LayoutDashboard className="w-5 h-5" />
                  <span className="text-xs font-[500] text-center">
                    {user?.role == "portal_access"
                      ? "Shared Careplans"
                      : "Dashboard"}{" "}
                  </span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {user?.role != "portal_access" && (
              <>
                {IS_PATIENT ? (
                  <>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={isPathActive("/patient-careplan")}
                        onClick={() => navigate("/patient-careplan")}
                        className={`w-full h-16 rounded-none p-0 ${
                          isPathActive("/patient-careplan")
                            ? "bg-primary/10 text-primary"
                            : ""
                        }`}
                      >
                        <div className="flex flex-row md:flex-col items-center justify-center gap-2 md:gap-1 w-full h-full">
                          <FileText className="w-5 h-5" />
                          <span className="text-xs font-[500]">My Plan</span>
                        </div>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={isPathActive("/patient-device")}
                        onClick={() => navigate("/patient-device")}
                        className={`w-full h-16 rounded-none p-0 ${
                          isPathActive("/patient-device")
                            ? "bg-primary/10 text-primary"
                            : ""
                        }`}
                      >
                        <div className="flex flex-row md:flex-col items-center justify-center gap-2 md:gap-1 w-full h-full">
                          <Smartphone className="w-5 h-5" />
                          <span className="text-xs font-[500]">Device</span>
                        </div>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    {/* <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={isPathActive("/alerts")}
                        onClick={() => navigate("/alerts")}
                        className={`w-full h-16 rounded-none p-0 relative ${isPathActive("/alerts")
                          ? "bg-primary/10 text-primary"
                          : ""
                          }`}
                      >
                        <div className="flex flex-row md:flex-col items-center justify-center gap-2 md:gap-1 w-full h-full">
                          <Bell className="w-5 h-5" />
                          <span className="text-xs font-[500]">Triage</span>
                        </div>
                      </SidebarMenuButton>
                    </SidebarMenuItem> */}
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={isPathActive("/patient-support")}
                        onClick={() => navigate("/patient-support")}
                        className={`w-full h-16 rounded-none p-0 ${
                          isPathActive("/patient-support")
                            ? "bg-primary/10 text-primary"
                            : ""
                        }`}
                      >
                        <div className="flex flex-row md:flex-col items-center justify-center gap-2 md:gap-1 w-full h-full">
                          <HelpCircle className="w-5 h-5" />
                          <span className="text-xs font-[500]">Support</span>
                        </div>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={isPathActive("/patient-manage-access")}
                        onClick={() => navigate("/patient-manage-access")}
                        className={`w-full h-16 rounded-none p-0 ${
                          isPathActive("/patient-manage-access")
                            ? "bg-primary/10 text-primary"
                            : ""
                        }`}
                      >
                        <div className="flex flex-row md:flex-col items-center justify-center gap-2 md:gap-1 w-full h-full">
                          <Users className="w-5 h-5" />
                          <span className="text-xs font-[500]">
                            Manage Access
                          </span>
                        </div>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    {patientHasLegacyData && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          isActive={isPathActive("/legacy")}
                          onClick={() => navigate("/legacy")}
                          className={`w-full h-16 rounded-none p-0 ${
                            isPathActive("/legacy")
                              ? "bg-primary/10 text-primary"
                              : ""
                          }`}
                        >
                          <div className="flex flex-row md:flex-col items-center justify-center gap-2 md:gap-1 w-full h-full">
                            <FileText className="w-5 h-5" />
                            <span className="text-xs font-medium text-center">
                              Past Medical Records
                            </span>
                          </div>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}
                  </>
                ) : (
                  <>
                    {(isPermissionNavRole
                      ? canAccess("canAccessClinicians")
                      : isAuthorized("canViewProviderInSidebar")) && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          isActive={
                            isPathActive("/practitioners") ||
                            isPathActive("/providers")
                          }
                          onClick={() => navigate("/providers")}
                          className={`w-full h-16 rounded-none p-0 ${
                            isPathActive("/practitioners") ||
                            isPathActive("/providers")
                              ? "bg-primary/10 text-primary"
                              : ""
                          }`}
                        >
                          <div className="flex flex-col items-center justify-center gap-1 w-full h-full">
                            <Stethoscope className="w-5 h-5" />
                            <span className="text-sm text-xs font-[500]">
                              Clinicians
                            </span>
                          </div>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}

                    {/* {isAuthorized("canViewProviderInSidebar") && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          isActive={isPathActive("/practitioners")}
                          onClick={() => navigate("/practitioners")}
                          className={`w-full h-16 rounded-none p-0 ${
                            isPathActive("/practitioners")
                              ? "bg-primary/10 text-primary"
                              : ""
                          }`}
                        >
                          <div className="flex flex-col items-center justify-center gap-1 w-full h-full">
                            <UserCheck className="w-5 h-5" />
                            <span className="text-sm">Practitioners</span>
                          </div>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )} */}

                    {(!isPermissionNavRole ||
                      canAccess("canAccessPatients")) && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          isActive={isPathActive("/patients")}
                          onClick={() => navigate("/patients")}
                          className={`w-full h-16 rounded-none p-0 ${
                            isPathActive("/patients")
                              ? "bg-primary/10 text-primary"
                              : ""
                          }`}
                        >
                          <div className="flex flex-col items-center justify-center gap-1 w-full h-full">
                            <Users className="w-5 h-5" />
                            <span className="text-sm text-xs font-[500]">
                              Patients
                            </span>
                          </div>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}

                    {(!isPermissionNavRole ||
                      canAccess("canAccessPrograms")) && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          isActive={isPathActive("/ccm-patients")}
                          onClick={() => navigate("/ccm-patients")}
                          className={`w-full h-16 rounded-none p-0 ${
                            isPathActive("/ccm-patients")
                              ? "bg-primary/10 text-primary"
                              : ""
                          }`}
                        >
                          <div className="flex flex-col items-center justify-center gap-1 w-full h-full">
                            <Pill className="w-5 h-5" />
                            <span className="text-sm text-center text-xs font-[500]">
                              Programs
                            </span>
                          </div>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}

                    {(!isPermissionNavRole || canAccess("canAccessTriage")) && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          isActive={isPathActive("/alerts")}
                          onClick={() => navigate("/alerts")}
                          className={`w-full h-16 rounded-none p-0 relative ${
                            isPathActive("/alerts")
                              ? "bg-primary/10 text-primary"
                              : ""
                          }`}
                        >
                          <div className="flex flex-col items-center justify-center gap-1 w-full h-full relative">
                            <div className="relative">
                              <Bell className="w-5 h-5" />
                              {criticalRecordsCount > 0 && (
                                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">
                                  {criticalRecordsCount > 99
                                    ? "99+"
                                    : criticalRecordsCount}
                                </span>
                              )}
                            </div>
                            <span className="text-sm text-center text-xs font-[500]">
                              Triage
                            </span>
                          </div>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}

                    {(user?.role === "admin" || user?.role === "caretaker") && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          isActive={isPathActive("/conflicts")}
                          onClick={() => navigate("/conflicts")}
                          className={`w-full h-16 rounded-none p-0 ${
                            isPathActive("/conflicts")
                              ? "bg-primary/10 text-primary"
                              : ""
                          }`}
                        >
                          <div className="flex flex-col items-center justify-center gap-1 w-full h-full">
                            <GitCompare className="w-5 h-5" />
                            <span className="text-sm text-xs font-[500]">
                              Conflicts
                            </span>
                          </div>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}

                    {(isPermissionNavRole
                      ? canAccess("canAccessEngage")
                      : user?.role === "admin" ||
                        user?.role === "caretaker") && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          isActive={isPathActive("/notifications")}
                          onClick={() => navigate("/notifications")}
                          className={`w-full h-16 rounded-none p-0 ${
                            isPathActive("/notifications")
                              ? "bg-primary/10 text-primary"
                              : ""
                          }`}
                        >
                          <div className="flex flex-col items-center justify-center gap-1 w-full h-full">
                            <Inbox className="w-5 h-5 shrink-0" aria-hidden />
                            <span className="text-sm text-xs font-[500]">
                              Engage
                            </span>
                          </div>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}

                    {(isPermissionNavRole
                      ? canAccess("canAccessAIChat")
                      : user?.role === "admin") && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          isActive={isPathActive("/ai-chat")}
                          onClick={() => navigate("/ai-chat")}
                          className={`w-full h-16 rounded-none p-0 ${
                            isPathActive("/ai-chat")
                              ? "bg-primary/10 text-primary"
                              : ""
                          }`}
                        >
                          <div className="flex flex-col items-center justify-center gap-1 w-full h-full">
                            <Sparkles className="w-5 h-5" />
                            <span className="text-sm text-xs font-[500]">
                              Anthro AI
                            </span>
                          </div>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}

                    {(isPermissionNavRole
                      ? canAccess("canAccessCaretakers")
                      : isAuthorized("canViewCaretakerInSidebar")) && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          isActive={isPathActive("/caretakers")}
                          onClick={() => navigate("/caretakers")}
                          className={`w-full h-16 rounded-none p-0 ${
                            isPathActive("/caretakers")
                              ? "bg-primary/10 text-primary"
                              : ""
                          }`}
                        >
                          <div className="flex flex-col items-center justify-center gap-1 w-full h-full">
                            <HeartHandshake className="w-5 h-5" />
                            <span className="text-sm text-xs font-[500]">
                              Caretakers
                            </span>
                          </div>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}

                    {showOrganizationsSidebarItem && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          isActive={isPathActive("/organizations")}
                          onClick={() => navigate("/organizations")}
                          className={`w-full h-16 rounded-none p-0 ${
                            isPathActive("/organizations")
                              ? "bg-primary/10 text-primary"
                              : ""
                          }`}
                        >
                          <div className="flex flex-col items-center justify-center gap-1 w-full h-full">
                            <Building className="w-5 h-5" />
                            <span className="text-sm text-xs font-[500]">
                              Organizations
                            </span>
                          </div>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}

                    {(isPermissionNavRole
                      ? canAccess("canAccessReports")
                      : isAuthorized("canViewOrganizationsInSidebar")) && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          isActive={isPathActive("/organization")}
                          onClick={() => onCallReportPage()}
                          className={`w-full h-16 rounded-none p-0 ${
                            isPathActive("/organization")
                              ? "bg-primary/10 text-primary"
                              : ""
                          }`}
                        >
                          <div className="flex flex-col items-center justify-center gap-1 w-full h-full">
                            <ClipboardList className="w-5 h-5" />
                            <span className="text-sm text-xs font-[500]">
                              Reports
                            </span>
                          </div>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}
                    {(!isPermissionNavRole || canAccess("canAccessSupport")) &&
                      !isProviderOrPractitioner && (
                        <SidebarMenuItem>
                          <SidebarMenuButton
                            isActive={isPathActive("/support")}
                            onClick={() => navigate("/support")}
                            className={`w-full h-16 rounded-none p-0 ${
                              isPathActive("/support")
                                ? "bg-primary/10 text-primary"
                                : ""
                            }`}
                          >
                            <div className="flex flex-col items-center justify-center gap-1 w-full h-full">
                              <Ticket className="w-5 h-5" />
                              <span className="text-sm text-xs font-[500]">
                                Support
                              </span>
                            </div>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      )}
                    {canAccessUserManagementNav && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          isActive={isPathActive("/user-management")}
                          onClick={() => navigate("/user-management")}
                          className={`w-full h-16 rounded-none p-0 ${
                            isPathActive("/user-management")
                              ? "bg-primary/10 text-primary"
                              : ""
                          }`}
                        >
                          <div className="flex flex-col items-center justify-center gap-1 w-full h-full px-0.5">
                            <UserCog className="w-5 h-5 shrink-0" aria-hidden />
                            <span className="text-[10px] font-[500] text-center leading-tight">
                              User
                              <span className="block">management</span>
                            </span>
                          </div>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}
                    {user?.role === "provider" && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          isActive={isPathActive("/providers/me/managers")}
                          onClick={() => navigate("/providers/me/managers")}
                          className={`w-full h-16 rounded-none p-0 ${
                            isPathActive("/providers/me/managers")
                              ? "bg-primary/10 text-primary"
                              : ""
                          }`}
                        >
                          <div className="flex flex-col items-center justify-center gap-1 w-full h-full px-0.5">
                            <Users className="w-5 h-5 shrink-0" aria-hidden />
                            <span className="text-[10px] font-[500] text-center leading-tight">
                              Managers
                            </span>
                          </div>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}
                    {!isPermissionNavRole ||
                    canAccess("canAccessPastMedicalRecords") ? (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          isActive={isPathActive("/legacy")}
                          onClick={() => navigate("/legacy")}
                          className={`w-full h-16 rounded-none p-0 ${
                            isPathActive("/legacy")
                              ? "bg-primary/10 text-primary"
                              : ""
                          }`}
                        >
                          <div className="flex flex-row md:flex-col items-center justify-center gap-2 md:gap-1 w-full h-full">
                            <FileText className="w-5 h-5" />
                            <span className="text-xs font-medium text-center">
                              Past Medical Records
                            </span>
                          </div>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ) : null}
                  </>
                )}
              </>
            )}
          </SidebarMenu>
        </SidebarContent>

        <SidebarFooter className="p-4 bg-white mt-auto list-none teachers">
          {canAccessConfigNav && (
            <SidebarMenuItem className="mb-0">
              <SidebarMenuButton
                isActive={isPathActive("/config")}
                onClick={() => navigate("/config")}
                className={`w-full h-16 rounded-none p-0 ${
                  isPathActive("/config") ? "bg-primary/10 text-primary" : ""
                }`}
              >
                <div className="flex flex-col items-center justify-center gap-1 w-full h-full">
                  <Settings className="w-5 h-5" />
                  <span className="text-sm text-xs font-[500]">Config</span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}

          <div className="flex items-center justify-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-14 w-14 rounded-full bg-gradient-to-br from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground font-bold text-2xl shadow-lg"
                >
                  {(displayName || "U").charAt(0).toUpperCase()}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  <User className="w-4 h-4 mr-2" /> Profile
                </DropdownMenuItem>
                {hasLinkedProfilesForSwitch && (
                  <DropdownMenuItem
                    onClick={() => setIsUserSwitchDialogOpen(true)}
                  >
                    <Repeat className="w-4 h-4 mr-2" /> Switch User
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => signOut(resetOrgs)}>
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </SidebarFooter>
      </Sidebar>

      {/*Top page Header */}

      <SidebarInset className="min-w-0 h-screen relative overflow-y-hidden  ">
        {/* First header only for mobile devices.... */}
        <header className="flex w-full h-16 items-center justify-between gap-2  border-b bg-background px-4 sticky top-0 z-50 overflow-x-auto display-none md:hidden">
          {/* NavIcon Toggle */}
          <div className="flex items-center gap-4 h-full md:hidden shrink-0">
            <SidebarTrigger />
            <Separator
              orientation="vertical"
              className="h-full block sm:hidden"
            />
          </div>

          <div
            className="flex items-center justify-center cursor-pointer "
            onClick={() => navigate("/")}
          >
            <img
              src={isMobile ? "/logo.png" : "/logo.png"}
              className="w-28 md:w-full"
              alt="Logo"
            />
          </div>
        </header>

        <header className="flex w-full h-12 md:h-16 items-center gap-2 sm:gap-4 border-b bg-background px-4 sm:px-4 sticky  md:top-0 md:z-50 overflow-x-auto">
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            {!isMainSidebarPage() && (
              <ArrowLeft
                className="w-5 h-5 text-muted-foreground cursor-pointer hover:scale-105"
                onClick={() => nav(-1)}
              />
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="truncate teachers font-bold text-xl">
                {location.pathname?.toLowerCase().includes("/alerts/view") ||
                activePage?.toLowerCase().includes("alerts/view")
                  ? "View Triage Details"
                  : location.pathname === "/alerts" || activePage === "/alerts"
                    ? "Triage"
                    : activePage
                          ?.toLowerCase()
                          .includes("view-legacy-careplan") ||
                        (location.pathname
                          .toLowerCase()
                          .includes("/legacy/care-plan/") &&
                          location.pathname.toLowerCase().endsWith("/view"))
                      ? "View Past Medical Careplan"
                      : activePage
                            ?.toLowerCase()
                            .includes("view-legacy-treatment-plan") ||
                          (location.pathname
                            .toLowerCase()
                            .includes("/legacy/treatment-plan/") &&
                            location.pathname.toLowerCase().endsWith("/view"))
                        ? "View Past Medical Treatment Plan"
                        : activePage?.toLowerCase().includes("legacy") ||
                            location.pathname.startsWith("/legacy")
                          ? "Past Medical Records"
                          : activePage?.toLowerCase().includes("dashboard")
                            ? user?.role === "portal_access"
                              ? "Shared Careplans"
                              : "Dashboard"
                            : activePage
                                  ?.toLowerCase()
                                  .includes("billing-analysis")
                              ? "Billing Analysis"
                              : activePage
                                    ?.toLowerCase()
                                    .includes("organizations")
                                ? "Organizations"
                                : activePage?.toLowerCase().includes("reports")
                                  ? "Reports"
                                  : activePage
                                        ?.toLowerCase()
                                        .includes("providers")
                                    ? "Clinicians"
                                    : activePage
                                          ?.toLowerCase()
                                          .includes("practitioners")
                                      ? "Practitioners"
                                      : activePage
                                            ?.toLowerCase()
                                            .includes("ccm-patients")
                                        ? "Programs"
                                        : activePage
                                              ?.toLowerCase()
                                              .includes("opt-out")
                                          ? "Opt Out Requests"
                                          : activePage
                                                ?.toLowerCase()
                                                .includes("/patients/add")
                                            ? "Add Patients"
                                            : activePage
                                                  ?.toLowerCase()
                                                  .includes("/patients/")
                                              ? "View Patients"
                                              : activePage
                                                    ?.toLowerCase()
                                                    .includes("patients")
                                                ? "Patients"
                                                : activePage
                                                      ?.toLowerCase()
                                                      .includes("caretakers")
                                                  ? "Caretakers"
                                                  : activePage
                                                        ?.toLowerCase()
                                                        .includes("config")
                                                    ? "Configuration"
                                                    : activePage
                                                          ?.toLowerCase()
                                                          .includes("terms")
                                                      ? "Terms & Conditions"
                                                      : activePage
                                                            ?.toLowerCase()
                                                            .includes("privacy")
                                                        ? "Privacy Profile"
                                                        : // view-careplan
                                                          activePage
                                                              ?.toLowerCase()
                                                              .includes(
                                                                "view-careplan",
                                                              )
                                                          ? "View Careplan"
                                                          : activePage
                                                                ?.toLowerCase()
                                                                .includes(
                                                                  "view-treatment-plan",
                                                                )
                                                            ? "View Treatment Plan"
                                                            : activePage
                                                                  ?.toLowerCase()
                                                                  .includes(
                                                                    "treatment plan",
                                                                  ) ||
                                                                activePage
                                                                  ?.toLowerCase()
                                                                  .includes(
                                                                    "treatment-plan",
                                                                  )
                                                              ? "Treatment Plan"
                                                              : activePage
                                                                    ?.toLowerCase()
                                                                    .includes(
                                                                      "careplan",
                                                                    )
                                                                ? "Careplan"
                                                                : activePage ===
                                                                    "/patient-device"
                                                                  ? "Device"
                                                                  : activePage ===
                                                                      "/patient-support"
                                                                    ? "Support"
                                                                    : activePage ===
                                                                        "/support"
                                                                      ? "Support"
                                                                      : activePage
                                                                            ?.toLowerCase()
                                                                            .includes(
                                                                              "user-management",
                                                                            ) ||
                                                                          location.pathname ===
                                                                            "/user-management"
                                                                        ? "User management"
                                                                        : activePage
                                                                              ?.toLowerCase()
                                                                              .includes(
                                                                                "providers/me/managers",
                                                                              ) ||
                                                                            location.pathname ===
                                                                              "/providers/me/managers"
                                                                          ? "Managers"
                                                                          : activePage
                                                                                ?.toLowerCase()
                                                                                .includes(
                                                                                  "notifications",
                                                                                ) ||
                                                                              location.pathname ===
                                                                                "/notifications"
                                                                            ? "Engage"
                                                                            : activePage
                                                                                  ?.toLowerCase()
                                                                                  .includes(
                                                                                    "conflicts",
                                                                                  ) ||
                                                                                location.pathname ===
                                                                                  "/conflicts"
                                                                              ? "Conflicts"
                                                                              : "Dashboard"}
              </h1>
              {entityId && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 px-2 py-1 bg-muted/50 rounded-md border border-border/50 hover:bg-muted transition-colors group flex-shrink-0">
                    <span className="text-xs font-mono text-muted-foreground font-medium">
                      {entityId}
                    </span>
                    <button
                      onClick={handleCopyId}
                      className="p-0.5 rounded hover:bg-background transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
                      title="Copy ID"
                    >
                      {copied ? (
                        <Check className="w-3 h-3 text-green-600" />
                      ) : (
                        <Copy className="w-3 h-3 text-muted-foreground group-hover:text-foreground transition-colors" />
                      )}
                    </button>
                  </div>
                  {entityHeaderAfterId ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      {entityHeaderAfterId}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          {/* Scrollable rightcomponent */}
          <div className="flex-1 min-w-0 flex justify-end">
            <div className="flex items-center gap-2 w-full justify-end">
              {IS_CARETAKER && caretakerHistoryId && isAdminDashboardPage ? (
                <HoverTooltip content="Caretaker history" side="bottom">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    aria-label="Caretaker history"
                    onClick={() => setIsCaretakerHistoryOpen(true)}
                  >
                    <History className="h-5 w-5" />
                  </Button>
                </HoverTooltip>
              ) : null}
              {IS_PRACTITIONER &&
              practitionerHistoryId &&
              isAdminDashboardPage ? (
                <HoverTooltip content="Practitioner history" side="bottom">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    aria-label="Practitioner history"
                    onClick={() => setIsPractitionerHistoryOpen(true)}
                  >
                    <History className="h-5 w-5" />
                  </Button>
                </HoverTooltip>
              ) : null}
              {IS_PROVIDER && providerHistoryId && isAdminDashboardPage ? (
                <HoverTooltip content="Provider history" side="bottom">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    aria-label="Provider history"
                    onClick={() => setIsProviderHistoryOpen(true)}
                  >
                    <History className="h-5 w-5" />
                  </Button>
                </HoverTooltip>
              ) : null}
              {IS_PATIENT && patientHistoryId && isAdminDashboardPage ? (
                <HoverTooltip content="Patient history" side="bottom">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    aria-label="Patient history"
                    onClick={() => setIsPatientHistoryOpen(true)}
                  >
                    <History className="h-5 w-5" />
                  </Button>
                </HoverTooltip>
              ) : null}
              {isAdminUser && isAdminDashboardPage && (
                <HoverTooltip content="Activity timeline" side="bottom">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    aria-label="Activity timeline"
                    onClick={() => setIsAdminHistoryOpen(true)}
                  >
                    <History className="h-5 w-5" />
                  </Button>
                </HoverTooltip>
              )}
              {(isPermissionNavRole
                ? canAccess("canAccessPatients")
                : user?.role === "admin" ||
                  user?.role === "caretaker" ||
                  user?.role === "provider") && <GlobalPatientSearch />}
              {rightcomponent}
            </div>
          </div>
        </header>
        <main
          className={`flex-1  relative ${className ? className : "p-4 px-4"}  overflow-x-hidden overflow-y-auto `}
        >
          {renderChildren(children, selectedOrgId)}
        </main>
      </SidebarInset>

      <Dialog
        open={isUserSwitchDialogOpen}
        onOpenChange={setIsUserSwitchDialogOpen}
      >
        <MyDialogContent
          heading=""
          showCloseButton={false}
          closeOnOutsideClick={true}
          className="max-w-lg border-none bg-transparent p-0 shadow-none"
        >
          <UserSwitch
            embedded
            currentProfile={profile}
            onClose={() => setIsUserSwitchDialogOpen(false)}
          />
        </MyDialogContent>
      </Dialog>

      {isAdminUser && isAdminDashboardPage ? (
        <Dialog open={isAdminHistoryOpen} onOpenChange={setIsAdminHistoryOpen}>
          <MyDialogContent
            heading="Activity timeline"
            className="!h-[min(720px,85vh)] !w-[min(42rem,96vw)] !max-w-[42rem] [&>div]:!p-0 [&_[data-slot=dialog-close]]:top-4 [&_[data-slot=dialog-close]]:right-5 [&_[data-slot=dialog-header]]:shrink-0 [&_[data-slot=dialog-header]]:border-b [&_[data-slot=dialog-header]]:border-[#E5E7EB] [&_[data-slot=dialog-header]]:!px-5 [&_[data-slot=dialog-header]]:!py-4 [&_[data-slot=dialog-title]]:pr-8 [&_[data-slot=dialog-title]]:text-base [&_[data-slot=dialog-title]]:font-semibold [&_[data-slot=dialog-title]]:text-[#111827] [&>div:last-child]:!flex [&>div:last-child]:!min-h-0 [&>div:last-child]:!flex-1 [&>div:last-child]:!flex-col [&>div:last-child]:!overflow-hidden [&>div:last-child]:!p-0 [&>div:last-child]:px-5 [&>div:last-child]:pb-4"
          >
            <PatientHistoryTimeline
              variant="admin"
              enabled={isAdminHistoryOpen}
              organizationId={adminHistoryOrganizationId}
            />
          </MyDialogContent>
        </Dialog>
      ) : null}

      {IS_PATIENT && patientHistoryId ? (
        <Dialog
          open={isPatientHistoryOpen}
          onOpenChange={setIsPatientHistoryOpen}
        >
          <MyDialogContent
            heading="Patient history"
            className="!h-[min(720px,85vh)] !w-[min(42rem,96vw)] !max-w-[42rem] [&>div]:!p-0 [&_[data-slot=dialog-close]]:top-4 [&_[data-slot=dialog-close]]:right-5 [&_[data-slot=dialog-header]]:shrink-0 [&_[data-slot=dialog-header]]:border-b [&_[data-slot=dialog-header]]:border-[#E5E7EB] [&_[data-slot=dialog-header]]:!px-5 [&_[data-slot=dialog-header]]:!py-4 [&_[data-slot=dialog-title]]:pr-8 [&_[data-slot=dialog-title]]:text-base [&_[data-slot=dialog-title]]:font-semibold [&_[data-slot=dialog-title]]:text-[#111827] [&>div:last-child]:!flex [&>div:last-child]:!min-h-0 [&>div:last-child]:!flex-1 [&>div:last-child]:!flex-col [&>div:last-child]:!overflow-hidden [&>div:last-child]:!p-0 [&>div:last-child]:px-5 [&>div:last-child]:pb-4"
          >
            <PatientHistoryTimeline
              patientId={patientHistoryId}
              enabled={isPatientHistoryOpen}
            />
          </MyDialogContent>
        </Dialog>
      ) : null}

      {IS_PROVIDER && providerHistoryId ? (
        <Dialog
          open={isProviderHistoryOpen}
          onOpenChange={setIsProviderHistoryOpen}
        >
          <MyDialogContent
            heading="Provider history"
            className="!h-[min(720px,85vh)] !w-[min(42rem,96vw)] !max-w-[42rem] [&>div]:!p-0 [&_[data-slot=dialog-close]]:top-4 [&_[data-slot=dialog-close]]:right-5 [&_[data-slot=dialog-header]]:shrink-0 [&_[data-slot=dialog-header]]:border-b [&_[data-slot=dialog-header]]:border-[#E5E7EB] [&_[data-slot=dialog-header]]:!px-5 [&_[data-slot=dialog-header]]:!py-4 [&_[data-slot=dialog-title]]:pr-8 [&_[data-slot=dialog-title]]:text-base [&_[data-slot=dialog-title]]:font-semibold [&_[data-slot=dialog-title]]:text-[#111827] [&>div:last-child]:!flex [&>div:last-child]:!min-h-0 [&>div:last-child]:!flex-1 [&>div:last-child]:!flex-col [&>div:last-child]:!overflow-hidden [&>div:last-child]:!p-0 [&>div:last-child]:px-5 [&>div:last-child]:pb-4"
          >
            <PatientHistoryTimeline
              providerId={providerHistoryId}
              enabled={isProviderHistoryOpen}
            />
          </MyDialogContent>
        </Dialog>
      ) : null}

      {IS_PRACTITIONER && practitionerHistoryId ? (
        <Dialog
          open={isPractitionerHistoryOpen}
          onOpenChange={setIsPractitionerHistoryOpen}
        >
          <MyDialogContent
            heading="Practitioner history"
            className="!h-[min(720px,85vh)] !w-[min(42rem,96vw)] !max-w-[42rem] [&>div]:!p-0 [&_[data-slot=dialog-close]]:top-4 [&_[data-slot=dialog-close]]:right-5 [&_[data-slot=dialog-header]]:shrink-0 [&_[data-slot=dialog-header]]:border-b [&_[data-slot=dialog-header]]:border-[#E5E7EB] [&_[data-slot=dialog-header]]:!px-5 [&_[data-slot=dialog-header]]:!py-4 [&_[data-slot=dialog-title]]:pr-8 [&_[data-slot=dialog-title]]:text-base [&_[data-slot=dialog-title]]:font-semibold [&_[data-slot=dialog-title]]:text-[#111827] [&>div:last-child]:!flex [&>div:last-child]:!min-h-0 [&>div:last-child]:!flex-1 [&>div:last-child]:!flex-col [&>div:last-child]:!overflow-hidden [&>div:last-child]:!p-0 [&>div:last-child]:px-5 [&>div:last-child]:pb-4"
          >
            <PatientHistoryTimeline
              practitionerId={practitionerHistoryId}
              enabled={isPractitionerHistoryOpen}
            />
          </MyDialogContent>
        </Dialog>
      ) : null}

      {IS_CARETAKER && caretakerHistoryId ? (
        <Dialog
          open={isCaretakerHistoryOpen}
          onOpenChange={setIsCaretakerHistoryOpen}
        >
          <MyDialogContent
            heading="Caretaker history"
            className="!h-[min(720px,85vh)] !w-[min(42rem,96vw)] !max-w-[42rem] [&>div]:!p-0 [&_[data-slot=dialog-close]]:top-4 [&_[data-slot=dialog-close]]:right-5 [&_[data-slot=dialog-header]]:shrink-0 [&_[data-slot=dialog-header]]:border-b [&_[data-slot=dialog-header]]:border-[#E5E7EB] [&_[data-slot=dialog-header]]:!px-5 [&_[data-slot=dialog-header]]:!py-4 [&_[data-slot=dialog-title]]:pr-8 [&_[data-slot=dialog-title]]:text-base [&_[data-slot=dialog-title]]:font-semibold [&_[data-slot=dialog-title]]:text-[#111827] [&>div:last-child]:!flex [&>div:last-child]:!min-h-0 [&>div:last-child]:!flex-1 [&>div:last-child]:!flex-col [&>div:last-child]:!overflow-hidden [&>div:last-child]:!p-0 [&>div:last-child]:px-5 [&>div:last-child]:pb-4"
          >
            <PatientHistoryTimeline
              caretakerId={caretakerHistoryId}
              enabled={isCaretakerHistoryOpen}
            />
          </MyDialogContent>
        </Dialog>
      ) : null}
    </SidebarProvider>
  );
}
